/** Ayudas minimas para construir DOM sin framework y formatear numeros en espanol. */
import { SIN_AGUA_CERCA_M } from '../db/consultas';

type Hijo = Node | string | number | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  etiqueta: K,
  atributos?: Record<string, unknown> | null,
  ...hijos: Hijo[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(etiqueta);
  if (atributos) {
    for (const [k, v] of Object.entries(atributos)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') el.className = String(v);
      else if (k === 'style' && typeof v === 'string') el.setAttribute('style', v);
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k === 'dataset' && typeof v === 'object') Object.assign(el.dataset, v as Record<string, string>);
      else if (k in el && typeof v !== 'object') (el as unknown as Record<string, unknown>)[k] = v;
      else el.setAttribute(k, String(v));
    }
  }
  for (const hijo of hijos) {
    if (hijo === null || hijo === undefined || hijo === false) continue;
    el.append(hijo instanceof Node ? hijo : String(hijo));
  }
  return el;
}

/** Como `append`, pero ignora null/undefined/false. */
export function anadir(el: Element, ...hijos: Hijo[]): void {
  for (const hijo of hijos) {
    if (hijo === null || hijo === undefined || hijo === false) continue;
    el.append(hijo instanceof Node ? hijo : String(hijo));
  }
}

export function vaciar(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

const fmtEntero = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });
const fmtDecimal = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 });
const fmtEuros = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export function m2(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${fmtEntero.format(v)} m²`;
}

export function num(v: number | null | undefined, decimales = 0): string {
  if (v === null || v === undefined) return '—';
  return decimales ? fmtDecimal.format(v) : fmtEntero.format(v);
}

export function euros(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : fmtEuros.format(v);
}

export function km2(m2v: number): string {
  return `${fmtDecimal.format(m2v / 1e6)} km²`;
}

export function mb(v: number): string {
  return v >= 1024 ? `${fmtDecimal.format(v / 1024)} GB` : `${fmtEntero.format(v)} MB`;
}

/** "Río Guadiaro (35 m)", "arroyo sin nombre (120 m)", "más de 2 km" o "—" si no se ha calculado. */
export function textoAgua(distM: number | null | undefined, nombre: string | null | undefined): string {
  if (distM === null || distM === undefined) return '—';
  if (distM >= SIN_AGUA_CERCA_M) return 'más de 2 km';
  return `${nombre ?? 'arroyo sin nombre'} (${num(distM)} m)`;
}

export function fecha(ms: number): string {
  return new Date(ms).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

/** Descarga un texto o binario como fichero. */
export function descargarFichero(nombre: string, contenido: string | Blob, tipo = 'text/plain;charset=utf-8'): void {
  const blob = contenido instanceof Blob ? contenido : new Blob([contenido], { type: tipo });
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: nombre });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Retardo para eventos frecuentes (moveend, input). */
export function rebotar<A extends unknown[]>(fn: (...a: A) => void, ms: number): (...a: A) => void {
  let t: ReturnType<typeof setTimeout> | null = null;
  return (...a: A) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

export function idAleatorio(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}
